import { useState, useEffect, useCallback } from 'react';
import settingsService from '../../../services/settingsService';
import geocodingService from '../../../services/geocodingService';
import '../../../styles/contactSettings.css';

const ContactSettings = () => {
  const [formData, setFormData] = useState({
    // Información de contacto
    contact: {
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'España'
      },
      phone: '',
      email: '',
      schedule: {
        weekdays: '',
        weekends: '',
        holidays: ''
      }
    },
    // Redes sociales
    socialMedia: {
      facebook: {
        url: '',
        enabled: true
      },
      twitter: {
        url: '',
        enabled: true
      },
      instagram: {
        url: '',
        enabled: true
      },
      youtube: {
        url: 'https://www.youtube.com/@ProKidsExperience',
        enabled: true
      }
    },
    // Información de la empresa
    companyInfo: {
      name: '',
      description: '',
      website: ''
    },
    // Configuración del mapa
    mapSettings: {
      latitude: 40.4168,
      longitude: -3.7038,
      zoom: 15
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [geocodingMessage, setGeocodingMessage] = useState('');

  // Cargar configuración actual
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const result = await settingsService.getSettings();

      if (result.success && result.data) {
        setFormData(result.data);
      } else {
        setMessage({ text: 'No se pudieron cargar los datos de configuración', type: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);

      // Manejar diferentes tipos de errores
      if (error.type === 'UNAUTHORIZED') {
        setMessage({ text: 'Sesión expirada. Por favor, inicia sesión nuevamente.', type: 'error' });
      } else if (error.type === 'FORBIDDEN') {
        setMessage({ text: 'No tienes permisos para acceder a esta configuración.', type: 'error' });
      } else if (error.type === 'NETWORK_ERROR') {
        setMessage({ text: 'Error de conexión al cargar la configuración', type: 'error' });
      } else {
        setMessage({ text: error.message || 'Error al cargar la configuración', type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar coordenadas automáticamente desde la dirección
  const updateCoordinatesFromAddress = useCallback(async (address) => {
    if (!address || !address.street || !address.city) {
      return;
    }

    setGeocodingMessage('🔄 Obteniendo coordenadas...');

    try {
      const coordinates = await geocodingService.getCoordinatesFromAddress(address);

      setFormData((prevFormData) => ({
        ...prevFormData,
        mapSettings: {
          ...prevFormData.mapSettings,
          latitude: coordinates.lat,
          longitude: coordinates.lng
        }
      }));

      if (coordinates.lat === geocodingService.DEFAULT_COORDINATES.lat &&
          coordinates.lng === geocodingService.DEFAULT_COORDINATES.lng) {
        setGeocodingMessage('⚠️ No se pudieron obtener coordenadas exactas. Se usaron coordenadas por defecto.');
      } else {
        setGeocodingMessage('✅ Coordenadas obtenidas correctamente.');
      }
    } catch (error) {
      console.error('Error al obtener coordenadas:', error);
      setGeocodingMessage('❌ Error al obtener coordenadas. Se usarán las coordenadas por defecto.');

      // Usar coordenadas por defecto en caso de error
      setFormData((prevFormData) => ({
        ...prevFormData,
        mapSettings: {
          ...prevFormData.mapSettings,
          latitude: geocodingService.DEFAULT_COORDINATES.lat,
          longitude: geocodingService.DEFAULT_COORDINATES.lng
        }
      }));
    } finally {
      // Limpiar el mensaje después de 3 segundos
      setTimeout(() => {
        setGeocodingMessage('');
      }, 3000);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const keys = name.split('.');

    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      // Navegar hasta el penúltimo nivel
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      // Establecer el valor final
      const finalKey = keys[keys.length - 1];
      if (type === 'checkbox') {
        current[finalKey] = checked;
      } else if (type === 'number') {
        current[finalKey] = parseFloat(value) || 0;
      } else {
        current[finalKey] = value;
      }

      // Si se cambió un campo de dirección, actualizar coordenadas automáticamente
      if (keys[0] === 'contact' && keys[1] === 'address' &&
          (keys[2] === 'street' || keys[2] === 'city' || keys[2] === 'postalCode')) {
        // Usar un timeout para evitar múltiples llamadas mientras el usuario escribe
        clearTimeout(window.geocodingTimeout);
        window.geocodingTimeout = setTimeout(() => {
          updateCoordinatesFromAddress(newData.contact.address);
        }, 1000); // Esperar 1 segundo después de que el usuario deje de escribir
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const result = await settingsService.updateSettings(formData);

      if (result.success) {
        setMessage({ text: 'Configuración actualizada correctamente', type: 'success' });
        // Actualizar los datos con la respuesta del servidor
        if (result.data) {
          setFormData(result.data);
        }
      } else {
        setMessage({
          text: result.message || 'Error al actualizar la configuración',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);

      // Manejar diferentes tipos de errores
      if (error.type === 'UNAUTHORIZED') {
        setMessage({ text: 'Sesión expirada. Por favor, inicia sesión nuevamente.', type: 'error' });
      } else if (error.type === 'FORBIDDEN') {
        setMessage({ text: 'No tienes permisos para modificar esta configuración.', type: 'error' });
      } else if (error.type === 'NETWORK_ERROR') {
        setMessage({ text: 'Error de conexión al guardar', type: 'error' });
      } else {
        setMessage({ text: error.message || 'Error al guardar la configuración', type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner"></div>
        <p>Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="contact-settings">
      <div className="settings-header">
        <h2>Configuración de Contacto</h2>
        <p>Administra la información de contacto que se muestra en el footer y página de contacto</p>
      </div>

      {message.text && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Información de Contacto */}
        <div className="settings-card">
          <h3>Información de Contacto</h3>
          <p className="settings-description">
            📍 Las coordenadas del mapa se actualizan automáticamente basándose en la dirección introducida.
          </p>

          {geocodingMessage && (
            <div className={`geocoding-message ${geocodingMessage.includes('✅') ? 'success' : geocodingMessage.includes('⚠️') ? 'warning' : 'error'}`}>
              {geocodingMessage}
            </div>
          )}
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact.address.street">Dirección</label>
              <input
                type="text"
                id="contact.address.street"
                name="contact.address.street"
                value={formData.contact?.address?.street || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Calle Principal 123"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact.address.city">Ciudad</label>
              <input
                type="text"
                id="contact.address.city"
                name="contact.address.city"
                value={formData.contact?.address?.city || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Madrid"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="contact.address.postalCode">Código Postal</label>
              <input
                type="text"
                id="contact.address.postalCode"
                name="contact.address.postalCode"
                value={formData.contact?.address?.postalCode || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="28001"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact.phone">Teléfono</label>
              <input
                type="tel"
                id="contact.phone"
                name="contact.phone"
                value={formData.contact?.phone || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="+34 91 123 45 67"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="contact.email">Email</label>
              <input
                type="email"
                id="contact.email"
                name="contact.email"
                value={formData.contact?.email || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="info@prokidsapp.com"
                required
              />
            </div>
          </div>
        </div>

        {/* Horarios de Atención */}
        <div className="settings-card">
          <h3>Horarios de Atención</h3>
          
          <div className="form-group">
            <label htmlFor="contact.schedule.weekdays">Días Laborables</label>
            <input
              type="text"
              id="contact.schedule.weekdays"
              name="contact.schedule.weekdays"
              value={formData.contact?.schedule?.weekdays || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Lunes a Viernes: 9:00 - 18:00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact.schedule.weekends">Fines de Semana</label>
            <input
              type="text"
              id="contact.schedule.weekends"
              name="contact.schedule.weekends"
              value={formData.contact?.schedule?.weekends || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Sábados: 10:00 - 14:00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact.schedule.holidays">Festivos</label>
            <input
              type="text"
              id="contact.schedule.holidays"
              name="contact.schedule.holidays"
              value={formData.contact?.schedule?.holidays || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Domingos y festivos: Cerrado"
            />
          </div>
        </div>

        {/* Información de la Empresa */}
        <div className="settings-card">
          <h3>Información de la Empresa</h3>

          <div className="form-group">
            <label htmlFor="companyInfo.name">Nombre de la Empresa</label>
            <input
              type="text"
              id="companyInfo.name"
              name="companyInfo.name"
              value={formData.companyInfo?.name || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="ProKidsApp"
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyInfo.description">Descripción</label>
            <textarea
              id="companyInfo.description"
              name="companyInfo.description"
              value={formData.companyInfo?.description || ''}
              onChange={handleInputChange}
              className="form-control"
              rows="3"
              placeholder="Liga Prokids - Desarrollando jóvenes talentos"
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyInfo.website">Sitio Web</label>
            <input
              type="url"
              id="companyInfo.website"
              name="companyInfo.website"
              value={formData.companyInfo?.website || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="https://prokidsapp.com"
            />
          </div>
        </div>

        {/* Redes Sociales */}
        <div className="settings-card">
          <h3>Redes Sociales</h3>

          <div className="social-media-section">
            <div className="form-group social-media-item">
              <div className="social-header">
                <label htmlFor="socialMedia.facebook.url">Facebook</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="socialMedia.facebook.enabled"
                    name="socialMedia.facebook.enabled"
                    checked={formData.socialMedia?.facebook?.enabled || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  <label htmlFor="socialMedia.facebook.enabled" className="form-check-label">
                    Mostrar
                  </label>
                </div>
              </div>
              <input
                type="url"
                id="socialMedia.facebook.url"
                name="socialMedia.facebook.url"
                value={formData.socialMedia?.facebook?.url || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="https://www.facebook.com/prokids.experience"
              />
            </div>

            <div className="form-group social-media-item">
              <div className="social-header">
                <label htmlFor="socialMedia.twitter.url">Twitter</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="socialMedia.twitter.enabled"
                    name="socialMedia.twitter.enabled"
                    checked={formData.socialMedia?.twitter?.enabled || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  <label htmlFor="socialMedia.twitter.enabled" className="form-check-label">
                    Mostrar
                  </label>
                </div>
              </div>
              <input
                type="url"
                id="socialMedia.twitter.url"
                name="socialMedia.twitter.url"
                value={formData.socialMedia?.twitter?.url || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="https://twitter.com/prokidsapp"
              />
            </div>

            <div className="form-group social-media-item">
              <div className="social-header">
                <label htmlFor="socialMedia.instagram.url">Instagram</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="socialMedia.instagram.enabled"
                    name="socialMedia.instagram.enabled"
                    checked={formData.socialMedia?.instagram?.enabled || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  <label htmlFor="socialMedia.instagram.enabled" className="form-check-label">
                    Mostrar
                  </label>
                </div>
              </div>
              <input
                type="url"
                id="socialMedia.instagram.url"
                name="socialMedia.instagram.url"
                value={formData.socialMedia?.instagram?.url || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="https://www.instagram.com/prokids.experience?igsh=MXFwOGx2azU3bWwxYg=="
              />
            </div>

            <div className="form-group social-media-item">
              <div className="social-header">
                <label htmlFor="socialMedia.youtube.url">YouTube</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="socialMedia.youtube.enabled"
                    name="socialMedia.youtube.enabled"
                    checked={formData.socialMedia?.youtube?.enabled || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  <label htmlFor="socialMedia.youtube.enabled" className="form-check-label">
                    Mostrar
                  </label>
                </div>
              </div>
              <input
                type="url"
                id="socialMedia.youtube.url"
                name="socialMedia.youtube.url"
                value={formData.socialMedia?.youtube?.url || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="https://www.youtube.com/@ProKidsExperience"
              />
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactSettings;
